import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const ImprovedHero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const slideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const slides = [
    {
      image: "/chola-r1.jpg",
      title: "Find Your Dream Home",
      subtitle: "Discover premium properties in prime locations",
    },
    {
      image: "/chola-flames.png",
      title: "Luxury Living Awaits",
      subtitle: "Experience comfort and elegance in every detail",
    },
    {
      image: "/chola-meadows.png",
      title: "Investment Opportunities",
      subtitle: "Secure your future with smart real estate choices",
    },
  ];

  const nextSlide = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);

    setTimeout(() => {
      setIsAnimating(false);
    }, 700); // Match this with the CSS transition duration
  }, [isAnimating, slides.length]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

    setTimeout(() => {
      setIsAnimating(false);
    }, 700); // Match this with the CSS transition duration
  }, [isAnimating, slides.length]);

  const resetSlideTimer = useCallback(() => {
    if (slideTimerRef.current) {
      clearInterval(slideTimerRef.current);
    }

    slideTimerRef.current = setInterval(() => {
      nextSlide();
    }, 5000);
  }, [nextSlide]);

  useEffect(() => {
    resetSlideTimer();
    return () => {
      if (slideTimerRef.current) {
        clearInterval(slideTimerRef.current);
      }
    };
  }, [resetSlideTimer]);

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;

    setIsAnimating(true);
    setCurrentSlide(index);
    resetSlideTimer();

    setTimeout(() => {
      setIsAnimating(false);
    }, 700); // Match this with the CSS transition duration
  };

  return (
    <section className="relative h-screen">
      {/* Preload images to prevent flickering */}
      <div className="hidden">
        {slides.map((slide, index) => (
          <img key={`preload-${index}`} src={slide.image} alt="preload" />
        ))}
      </div>

      {/* Carousel container with fixed aspect ratio */}
      <div className="relative h-full w-full overflow-hidden">
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-700 ease-in-out ${getSlidePosition(
              index,
              currentSlide,
              slides.length
            )}`}
            aria-hidden={index !== currentSlide}
          >
            <div className="relative h-full w-full">
              <img
                src={slide.image}
                alt={slide.title}
                className="absolute inset-0 h-full w-full object-cover object-center"
                style={{ objectPosition: "50% 50%" }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
            </div>
          </div>
        ))}

        {/* Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-center text-white max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-lg transition-all duration-500 transform translate-y-0">
              {slides[currentSlide].title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-100 drop-shadow-md max-w-2xl mx-auto">
              {slides[currentSlide].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/properties"
                className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Browse Properties
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Previous slide"
          disabled={isAnimating}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Next slide"
          disabled={isAnimating}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Dot Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${
                index === currentSlide
                  ? "bg-white w-6"
                  : "bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? "true" : "false"}
              disabled={isAnimating}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Helper function to determine slide position
function getSlidePosition(
  index: number,
  currentSlide: number,
  totalSlides: number
): string {
  // Current slide is centered
  if (index === currentSlide) return "translate-x-0";

  // Next slide (to the right)
  if (index === (currentSlide + 1) % totalSlides) return "translate-x-full";

  // Previous slide (to the left)
  if (index === (currentSlide - 1 + totalSlides) % totalSlides)
    return "-translate-x-full";

  // All other slides, position them off-screen to the right
  return "translate-x-full";
}

export default ImprovedHero;
